import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin from 'wavesurfer.js/plugins/regions';
import { trimAndMergeAudio, checkTaskStatus } from '../../services/audioApi';
import { 
    Upload, Download, Loader2, AlertCircle, 
    CheckCircle, Play, Pause, Scissors, Trash2, ArrowUp, ArrowDown, Plus, MousePointerClick 
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';

const SubscribePrompt = () => {
    const { t } = useTranslation();
    const PLAN_PRICE = "14,90 €"; 

    return (
        <div className="flex items-center justify-center h-full p-4">
            <div className="max-w-md w-full bg-brand-dark border-2 border-brand-orange rounded-lg p-8 shadow-lg">
                <h2 className="text-center text-3xl font-bold text-brand-orange mb-4">
                    {PLAN_PRICE}/{t('subscribePrompt.perMonth')}
                </h2>
                <ul className="text-slate-200 space-y-3 list-disc list-inside mb-8">
                    <li>{t('subscribePrompt.feature1')}</li>
                    <li>{t('subscribePrompt.feature2')}</li>
                    <li>{t('subscribePrompt.feature3')}</li>
                    <li>{t('subscribePrompt.feature4')}</li>
                    <li>{t('subscribePrompt.feature5')}</li>
                </ul>
                <Link 
                    to="/pricing"
                    className="w-full block text-center px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
                >
                    {t('subscribePrompt.subscribeButton')}
                </Link>
            </div>
        </div>
    );
};

const PurchaseCreditsPrompt = () => {
    const { t } = useTranslation();
    const { user } = useAuth();

    return (
        <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <h2 className="text-2xl font-semibold mb-2 text-slate-800">{t('purchaseCreditsPrompt.title')}</h2>
            <p className="text-slate-800 mb-1">{t('purchaseCreditsPrompt.yourCredits')} <span className="text-brand-orange font-bold">{user?.credits || 0}</span></p>
            <p className="text-slate-800 mb-6 max-w-md">{t('purchaseCreditsPrompt.description')}</p>
            <Link 
                to="/purchase-credits"
                className="px-8 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
            >
                {t('purchaseCreditsPrompt.purchaseButton')}
            </Link>
        </div>
    );
};

const AudioTrimmer = () => {
    const { t } = useTranslation();
    const { user, setCredits } = useAuth();
    
    // --- STATE ---
    const [file, setFile] = useState(null);
    const [audioUrl, setAudioUrl] = useState(null);
    const [error, setError] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isWaveformReady, setIsWaveformReady] = useState(false);
    
    // Segments & Selection
    const [segments, setSegments] = useState([]);
    const [currentSelection, setCurrentSelection] = useState({ start: 0, end: 0 });
    const [playingSegmentId, setPlayingSegmentId] = useState(null); // Track which segment is previewing

    // Processing
    const [isProcessing, setIsProcessing] = useState(false);
    const [taskId, setTaskId] = useState(null);
    const [progressStep, setProgressStep] = useState('');
    const [progressValue, setProgressValue] = useState(0);
    const [resultUrl, setResultUrl] = useState(null);

    // Refs
    const [waveformContainer, setWaveformContainer] = useState(null);
    const wavesurferRef = useRef(null);
    const regionsRef = useRef(null);
    const pollingIntervalRef = useRef(null);

    // Cleanup
    useEffect(() => {
        return () => {
            if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
            if (wavesurferRef.current) wavesurferRef.current.destroy();
            if (audioUrl) URL.revokeObjectURL(audioUrl);
        };
    }, []);

    // --- WAVESURFER INITIALIZATION ---
    useEffect(() => {
        if (!audioUrl || !waveformContainer) return;

        console.log('🎵 Initializing WaveSurfer...');
        setIsWaveformReady(false);
        setError(null);

        if (wavesurferRef.current) {
            wavesurferRef.current.destroy();
            wavesurferRef.current = null;
        }

        try {
            const wsRegions = RegionsPlugin.create({
                dragSelection: {
                    slop: 5, 
                    color: 'rgba(234, 88, 12, 0.4)',
                },
            });
            regionsRef.current = wsRegions;

            const ws = WaveSurfer.create({
                container: waveformContainer,
                url: audioUrl,
                waveColor: '#475569',
                progressColor: '#ea580c',
                cursorColor: '#ea580c',
                cursorWidth: 2,
                height: 128,
                barWidth: 2,
                barGap: 1,
                barRadius: 2,
                normalize: true,
                minPxPerSec: 100,
                fillParent: true,
                interact: true,
                plugins: [wsRegions],
            });

            // Events
            ws.on('ready', () => {
                console.log('✅ WaveSurfer ready!');
                setIsWaveformReady(true);
                wavesurferRef.current = ws;
            });

            ws.on('error', (err) => {
                console.error('❌ WaveSurfer error:', err);
                setError(`Failed to load audio: ${err.message || err}`);
            });

            ws.on('play', () => setIsPlaying(true));
            
            // IMPORTANT: When pause happens, reset both main state and segment state
            ws.on('pause', () => {
                setIsPlaying(false);
                setPlayingSegmentId(null); 
            });
            
            ws.on('finish', () => {
                setIsPlaying(false);
                setPlayingSegmentId(null);
            });

            // Region Logic
            wsRegions.on('region-created', (region) => {
                const regions = wsRegions.getRegions();
                regions.forEach(r => {
                    if (r.id !== region.id) r.remove();
                });
                setCurrentSelection({ start: region.start, end: region.end });
            });

            wsRegions.on('region-updated', (region) => {
                setCurrentSelection({ start: region.start, end: region.end });
            });

            wsRegions.on('region-clicked', (region, e) => {
                e.stopPropagation();
                region.play();
            });

        } catch (err) {
            console.error('❌ Error creating WaveSurfer:', err);
            setError(`Failed to initialize: ${err.message}`);
        }

        return () => {
            if (wavesurferRef.current) {
                wavesurferRef.current.destroy();
                wavesurferRef.current = null;
            }
        };

    }, [audioUrl, waveformContainer]);

    // --- HELPER: Manual Selection ---
    const createDefaultSelection = () => {
        if (!wavesurferRef.current || !regionsRef.current) return;
        const duration = wavesurferRef.current.getDuration();
        if (duration < 1) return;
        regionsRef.current.clearRegions();
        const start = duration * 0.2;
        const end = duration * 0.4;
        regionsRef.current.addRegion({
            start: start,
            end: end,
            color: 'rgba(234, 88, 12, 0.4)',
            drag: true,
            resize: true
        });
        setCurrentSelection({ start, end });
    };

    const playSelection = () => {
        if (!regionsRef.current) return;
        const regions = regionsRef.current.getRegions();
        if (regions.length > 0) {
            regions[0].play();
        }
    };

    // --- NEW: Play a Specific Segment from the list ---
    const handleSegmentPlay = (segment) => {
        if (!wavesurferRef.current) return;

        // If this segment is already playing, pause it
        if (playingSegmentId === segment.id) {
            wavesurferRef.current.pause();
            return; // state update handled by 'pause' event listener
        }

        // Play the range (start, end)
        // WaveSurfer will handle playing only this slice
        wavesurferRef.current.play(segment.start, segment.end);
        setPlayingSegmentId(segment.id);
    };


    // --- API & State Logic ---
    useEffect(() => {
        if (taskId && isProcessing) {
            pollingIntervalRef.current = setInterval(async () => {
                try {
                    const data = await checkTaskStatus(taskId);
                    if (data.state === 'SUCCESS' || data.status === 'completed') {
                        clearInterval(pollingIntervalRef.current);
                        const payload = data.result;
			const innerResult = payload.result;
			if (innerResult?.new_credits !== undefined) {
                            setCredits(innerResult.new_credits);
                        }
                        if (payload.status === 'completed' && innerResult?.download_url) {
                            setResultUrl(innerResult.download_url);
                            setProgressValue(100);
                            setIsProcessing(false);
                        } else {
                            setError(payload.error || t('audioTrimmer.errors.generic'));
                            setIsProcessing(false);
                        }
                    } else if (data.state === 'FAILURE' || data.status === 'failed') {
                        clearInterval(pollingIntervalRef.current);
                        setError(data.error || t('audioTrimmer.errors.generic'));
                        setIsProcessing(false);
                    } else {
                        if (data.info) {
                            if (data.info.step) setProgressStep(data.info.step);
                            if (data.info.progress) setProgressValue(data.info.progress);
                        }
                    }
                } catch (err) {
                    clearInterval(pollingIntervalRef.current);
                    setError(err.message);
                    setIsProcessing(false);
                }
            }, 2000);
        }
        return () => {
            if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
        };
    }, [taskId, isProcessing, t]);

    const handleFileChange = (e) => {
        const selected = e.target.files[0];
        if (selected) {
            if (selected.size > 50 * 1024 * 1024) {
                setError(t('audioTrimmer.errors.tooLarge'));
                return;
            }
            if (audioUrl) URL.revokeObjectURL(audioUrl);
            setFile(selected);
            setSegments([]);
            setResultUrl(null);
            setError(null);
            setCurrentSelection({ start: 0, end: 0 });
            setPlayingSegmentId(null);
            setWaveformContainer(null);
            setIsWaveformReady(false);
            const objectUrl = URL.createObjectURL(selected);
            setAudioUrl(objectUrl);
        }
    };

    const togglePlayPause = () => {
        if (wavesurferRef.current) wavesurferRef.current.playPause();
    };

    const addSegment = () => {
        const duration = currentSelection.end - currentSelection.start;
        if (duration < 0.1) {
            setError(t('audioTrimmer.errors.selectionTooSmall') || 'Selection too small');
            return;
        }
        const newSegment = { id: Date.now(), start: currentSelection.start, end: currentSelection.end };
        setSegments([...segments, newSegment]);
        setError(null);
        if (regionsRef.current) regionsRef.current.clearRegions();
        setCurrentSelection({ start: 0, end: 0 });
    };

    const removeSegment = (id) => setSegments(segments.filter(s => s.id !== id));

    const moveSegment = (index, direction) => {
        const newSegments = [...segments];
        const targetIndex = index + direction;
        if (targetIndex >= 0 && targetIndex < newSegments.length) {
            [newSegments[index], newSegments[targetIndex]] = [newSegments[targetIndex], newSegments[index]];
            setSegments(newSegments);
        }
    };

    const handleProcess = async () => {
        if (!file || segments.length === 0) {
            setError(t('audioTrimmer.errors.noSegments'));
            return;
        }
        setIsProcessing(true);
        setError(null);
        setProgressValue(0);
        const formattedSegments = segments.map(s => [s.start, s.end]);
        try {
            const response = await trimAndMergeAudio(file, formattedSegments);
            setTaskId(response.task_id);
        } catch (err) {
            setError(err.message);
            setIsProcessing(false);
        }
    };

    const handleClear = () => {
        if (audioUrl) URL.revokeObjectURL(audioUrl);
        if (wavesurferRef.current) {
            wavesurferRef.current.destroy();
            wavesurferRef.current = null;
        }
        setFile(null);
        setAudioUrl(null);
        setSegments([]);
        setResultUrl(null);
        setError(null);
        setCurrentSelection({ start: 0, end: 0 });
        setPlayingSegmentId(null);
        setWaveformContainer(null);
        setIsWaveformReady(false);
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = (seconds % 60).toFixed(2);
        return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    };

    const getProgressLabel = (step) => {
        const map = {
            'downloading': t('audioTrimmer.steps.downloading'),
            'processing': t('audioTrimmer.steps.processing'),
            'encoding': t('audioTrimmer.steps.encoding'),
            'uploading': t('audioTrimmer.steps.uploading')
        };
        return map[step] || t('audioTrimmer.processing');
    };

    // --- RENDER ---
    return (
        <div className="p-6 md:p-8 h-full overflow-y-auto text-brand-dark bg-brand-white">
            { (user?.subscription_status !== 'active' && (!user?.is_verified || user?.credits <= 2)) ? (
                <SubscribePrompt />
            ) : user?.credits <= 0 ? (
                <PurchaseCreditsPrompt />
            ) : resultUrl ? (
                // --- RESULT VIEW ---
                <div className="max-w-2xl mx-auto text-center mt-10">
                    <div className="bg-green-50 border border-green-200 p-8 rounded-lg mb-8">
                        <CheckCircle className="text-green-500 h-16 w-16 mx-auto mb-4" />
                        <h3 className="text-2xl font-bold text-green-800 mb-2">{t('audioTrimmer.success')}</h3>
                        <div className="bg-white p-4 rounded-md shadow-sm mt-6">
                            <audio controls src={resultUrl} className="w-full" controlsList="nodownload" />
                        </div>
                        <a 
                            href={resultUrl} 
                            download="trimmed_merged_audio.mp3"
                            className="mt-8 inline-flex items-center px-8 py-3 bg-brand-orange text-white font-bold rounded-md hover:bg-orange-600 transition shadow-lg"
                        >
                            <Download className="mr-2" size={20} /> {t('audioTrimmer.download')}
                        </a>
                    </div>
                    <button onClick={handleClear} className="text-slate-600 hover:text-brand-orange underline">
                        {t('audioTrimmer.processAnother')}
                    </button>
                </div>
            ) : (
                // --- EDITOR VIEW ---
                <div className="max-w-4xl mx-auto">
                    <h2 className="text-2xl font-semibold mb-2 text-brand-orange">{t('audioTrimmer.title')}</h2>
                    <p className="mt-2 text-brand-dark mb-6">{t('audioTrimmer.description')}</p>

                    <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200 mb-6">
                        <div className="flex items-center p-6 border-2 border-dashed border-slate-300 rounded-md bg-slate-50 cursor-pointer hover:bg-slate-100 transition">
                             <label className="w-full h-full flex flex-col items-center justify-center cursor-pointer">
                                <Upload className="h-10 w-10 text-slate-400 mb-2" />
                                <span className="text-lg text-brand-orange font-semibold">
                                    {file ? file.name : t('audioTrimmer.selectFile')}
                                </span>
                                <input type="file" className="sr-only" onChange={handleFileChange} accept="audio/*" />
                             </label>
                        </div>
                    </div>

                    {file && (
                        <div className="space-y-6">
                            {/* Editor Area */}
                            <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-200">
                                <div className="mb-4 flex justify-between items-center">
                                    <h3 className="font-semibold text-brand-dark">
                                        {t('audioTrimmer.editorTitle')}
                                    </h3>
                                    
                                    <div className="flex gap-2">
                                        <button 
                                            onClick={createDefaultSelection}
                                            disabled={!isWaveformReady}
                                            className="px-3 py-2 bg-slate-100 text-slate-700 rounded-md hover:bg-slate-200 transition text-sm flex items-center"
                                        >
                                            <MousePointerClick size={16} className="mr-2" /> Select Region
                                        </button>

                                        <button 
                                            onClick={togglePlayPause}
                                            disabled={!isWaveformReady}
                                            className="p-2 bg-brand-orange text-white rounded-full hover:bg-orange-600 transition disabled:bg-slate-300"
                                        >
                                            {isPlaying && playingSegmentId === null ? <Pause size={20} /> : <Play size={20} />}
                                        </button>
                                    </div>
                                </div>
                                
                                <div className="relative w-full h-[128px] mb-4 border-2 border-slate-300 rounded bg-white">
                                    <div ref={setWaveformContainer} className="w-full h-full block"/>
                                    {!isWaveformReady && (
                                        <div className="absolute inset-0 flex items-center justify-center bg-white/90 backdrop-blur-sm z-10">
                                            <Loader2 className="animate-spin mr-2" size={24} /> <span>Loading...</span>
                                        </div>
                                    )}
                                </div>

                                <div className="flex flex-col md:flex-row items-center justify-between bg-slate-50 p-4 rounded-md gap-4">
                                    <div className="text-sm font-mono text-slate-700">
                                        Selection: 
                                        <span className="text-brand-orange font-bold ml-2">{formatTime(currentSelection.start)}</span> 
                                        <span className="mx-1">-</span>
                                        <span className="text-brand-orange font-bold">{formatTime(currentSelection.end)}</span>
                                        <span className="text-xs text-slate-500 ml-2">
                                            ({(currentSelection.end - currentSelection.start).toFixed(2)}s)
                                        </span>
                                    </div>
                                    
                                    <div className="flex gap-2">
                                        <button
                                            onClick={playSelection}
                                            disabled={currentSelection.end - currentSelection.start < 0.1}
                                            className="px-3 py-2 bg-slate-200 text-slate-700 rounded-md hover:bg-slate-300 transition text-sm disabled:opacity-50"
                                            title="Preview Selection"
                                        >
                                            <Play size={16} />
                                        </button>

                                        <button 
                                            onClick={addSegment} 
                                            disabled={!isWaveformReady || currentSelection.end - currentSelection.start < 0.1}
                                            className="flex items-center px-4 py-2 bg-slate-800 text-white rounded-md hover:bg-black transition disabled:bg-slate-400 disabled:cursor-not-allowed"
                                        >
                                            <Plus size={16} className="mr-2" /> {t('audioTrimmer.addSegment')}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* --- Segments List with PREVIEW --- */}
                            {segments.length > 0 && (
                                <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-200">
                                    <h3 className="font-semibold text-brand-dark mb-4">{t('audioTrimmer.segmentsList')} ({segments.length})</h3>
                                    <div className="space-y-2">
                                        {segments.map((seg, idx) => (
                                            <div key={seg.id} className="flex items-center justify-between p-3 bg-slate-50 rounded border border-slate-200 hover:border-brand-orange/50 transition-colors">
                                                <div className="flex items-center">
                                                    <span className="bg-brand-orange text-white text-xs px-2 py-1 rounded mr-3">#{idx + 1}</span>
                                                    
                                                    {/* PREVIEW BUTTON */}
                                                    <button 
                                                        onClick={() => handleSegmentPlay(seg)}
                                                        className="mr-3 p-2 rounded-full bg-slate-100 hover:bg-brand-orange hover:text-white transition text-slate-600"
                                                        title="Preview Segment"
                                                    >
                                                        {playingSegmentId === seg.id ? <Pause size={14} /> : <Play size={14} />}
                                                    </button>

                                                    <span className="font-mono text-sm">{formatTime(seg.start)} ➔ {formatTime(seg.end)}</span>
                                                    <span className="text-xs text-slate-400 ml-2">({(seg.end - seg.start).toFixed(2)}s)</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <button onClick={() => moveSegment(idx, -1)} disabled={idx === 0} className="p-1 text-slate-500 hover:text-brand-orange disabled:opacity-30"><ArrowUp size={16} /></button>
                                                    <button onClick={() => moveSegment(idx, 1)} disabled={idx === segments.length - 1} className="p-1 text-slate-500 hover:text-brand-orange disabled:opacity-30"><ArrowDown size={16} /></button>
                                                    <button onClick={() => removeSegment(seg.id)} className="p-1 text-red-500 hover:bg-red-50 rounded"><Trash2 size={16} /></button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {error && (
                                <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                                    <p className="text-red-700 flex items-center"><AlertCircle size={16} className="mr-2"/>{error}</p>
                                </div>
                            )}

                            <button
                                onClick={handleProcess}
                                disabled={isProcessing || segments.length === 0}
                                className="w-full px-8 py-4 bg-brand-orange text-white font-bold text-lg rounded-md hover:bg-orange-600 transition-colors disabled:bg-slate-400 disabled:cursor-not-allowed flex items-center justify-center"
                            >
                                {isProcessing ? (
                                    <><Loader2 className="animate-spin mr-2" /> {t('audioTrimmer.processing')}</>
                                ) : (
                                    <><Scissors className="mr-2" /> {t('audioTrimmer.mergeButton')}</>
                                )}
                            </button>

                             {isProcessing && (
                                <div className="mt-4">
                                    <div className="flex justify-between text-xs text-slate-500 mb-1">
                                        <span>{getProgressLabel(progressStep)}</span>
                                        <span>{progressValue}%</span>
                                    </div>
                                    <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                                        <div className="bg-brand-orange h-2 rounded-full transition-all duration-500" style={{ width: `${progressValue}%` }}></div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AudioTrimmer;
